'use client';

import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RangeGroup } from '@/components/RangeGroup/RangeGroup';
import {
  LimitTypesEnum,
  MAX_SALARY_VALUE,
  MIN_SALARY_VALUE
} from '@/constants/common';
import { debounce } from '@/utils/helpers/debounce';

import * as S from './SalaryFilter.styles';

interface FormData {
  salaryFrom: number;
  salaryTo: number;
}

export const SalaryFilter = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue, watch } = useFormContext<FormData>();
  const [localeValue, setLocaleValue] = useState<FormData>({
    salaryFrom: MIN_SALARY_VALUE,
    salaryTo: MAX_SALARY_VALUE
  });
  const [salaryFrom, salaryTo] = watch(['salaryFrom', 'salaryTo']);

  useEffect(() => {
    if (
      localeValue.salaryFrom === salaryFrom &&
      localeValue.salaryTo === salaryTo
    )
      return;

    setLocaleValue({ salaryFrom, salaryTo });
  }, [salaryFrom, salaryTo]);

  useEffect(() => {
    const values = getValues();
    setLocaleValue({
      salaryFrom: values.salaryFrom,
      salaryTo: values.salaryTo
    });
  }, [getValues]);

  const debounceChange = useCallback(
    debounce((name: keyof FormData, value: number) => {
      setValue(name, value);
    }, 100),
    [setValue]
  );

  const handleBlur = () => {
    debounceChange.cancel();

    const { salaryFrom: salaryFromState, salaryTo: salaryToState } =
      localeValue;

    const computedValue =
      salaryFromState > salaryToState ? salaryFromState : salaryToState;

    setLocaleValue(prev => {
      const result = { ...prev, salaryTo: computedValue };
      setValue('salaryTo', result.salaryTo);
      setValue('salaryFrom', result.salaryFrom);
      return result;
    });
  };

  const createSalaryHandler = (
    fieldName: keyof FormData,
    value: number,
    max?: boolean
  ) => {
    setLocaleValue(prev => {
      const result = { ...prev, [fieldName]: value };

      if (max ? prev.salaryFrom < value : prev.salaryTo > value) {
        debounceChange(fieldName, result[fieldName]);
      }

      return result;
    });
  };

  return (
    <S.RangeSliderWrapper>
      <Controller
        name="salaryFrom"
        control={control}
        render={({ field, fieldState }) => {
          return (
            <RangeGroup
              type={LimitTypesEnum.MIN}
              label={t('Filter.from')}
              min={MIN_SALARY_VALUE}
              max={MAX_SALARY_VALUE}
              isTouched={fieldState.isTouched}
              value={localeValue[field.name]}
              onBlur={() => handleBlur()}
              onChange={value => createSalaryHandler(field.name, value)}
            />
          );
        }}
      />

      <Controller
        name="salaryTo"
        control={control}
        render={renderProps => {
          const { field, fieldState } = renderProps;

          return (
            <RangeGroup
              type={LimitTypesEnum.MAX}
              label={t('Filter.to')}
              min={MIN_SALARY_VALUE}
              max={MAX_SALARY_VALUE}
              value={localeValue[field.name]}
              isTouched={fieldState.isTouched}
              onBlur={() => {
                field.onBlur();
                handleBlur();
              }}
              onChange={value => createSalaryHandler(field.name, value, true)}
            />
          );
        }}
      />
    </S.RangeSliderWrapper>
  );
};
